import { parseUnits } from 'ethers/lib/utils';
import { CrossIcon, EditIcon, ShareIcon, TokenIcons } from '../../../../assets/icons';
import Button from '../../../../components/Button';
import Card from '../../../../components/Card';
import { Tokens } from '../../../../config/constants';
import { Modals } from '../../../../context/modal/modalTypes';
import useModalState from '../../../../context/modal/useModalState';
import { calculatePnl, formatAmountWithSuffix, formatUSD } from '../../../../helpers';
import {
    UserPosition,
    UserPositionRaw,
} from '../../../../hooks/web3/trade/useUserPositions';
import AssetType from '../../../../components/AssetType';
import clsx from 'clsx';
import MarkPrice from '../MarkPrice';
import { ShareModalType } from '../../../../modals/Share/ShareModal';
import { useWeb3React } from '@web3-react/core';
import useUserCreatedReferral from '../../../../hooks/referral/useUserCreatedReferral';
import useTokenStats from '../../../../hooks/web3/useTokenStats';
import React, { useState, useRef } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { PnlValues } from '../../../../helpers/calculations/calculatePnl';
import { config } from '../../../../config/config';

type PositionRowProps = {
    position: UserPositionRaw;
};

type PnlValueWrapperProps = {
    position: UserPositionRaw;
    pnlValues: PnlValues | undefined;
    handleShare: (e: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
};

const PositionRow: React.FC<PositionRowProps> = ({ position }) => {
    const { openModal, setModalData, modal } = useModalState();
    const [isActionShown, setShowActions] = useState(false);
    const [visibility, setVisibility] = useState(false);

    const cardRef = useRef<HTMLDivElement>(null);

    const otherModalOpen = Object.values(modal).includes(true);

    const tokenStats = useTokenStats();

    const pnlValues = calculatePnl(
        position,
        tokenStats?.[position.tradePair.chartSymbol]?.price
    );

    const showActions = () => {
        cardRef.current?.focus();
        setShowActions(true);
    };
    const hideActions = () => {
        setShowActions(false);
    };

    const handleTransitionEnd = () => {
        isActionShown && cardRef.current?.focus();
        setVisibility(isActionShown);
    };

    const handleRowClick = () => {
        setModalData((prev) => ({ ...prev, data: position }));
        openModal('positionSummaryModal');
    };

    const handleButtonClick = async (
        event: React.MouseEvent<HTMLButtonElement>,
        userPosition: UserPositionRaw,
        modalName: keyof Modals
    ) => {
        event.stopPropagation();

        setModalData((prev) => ({
            ...prev,
            data: {
                position: userPosition,
            },
        }));
        openModal(modalName);
    };

    const handleShare = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (!pnlValues || !tokenStats) return;

        try {
            const userPosition: UserPosition & { markPrice: string } = {
                ...position,
                ...pnlValues,
                markPrice: tokenStats?.[position.tradePair.chartSymbol]?.price,
            };

            openModal('shareOpenPositionModal');
            setModalData((prev) => ({
                ...prev,
                data: {
                    position: userPosition,
                    type: ShareModalType.OpenPosition,
                },
            }));
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <div className='row' onClick={handleRowClick}>
            <Card>
                <div className='positions__mark-price'>
                    {TokenIcons[position.tradePair?.asset.symbol ?? Tokens.FALLBACK]}
                    <span className='column-label'>
                        {position.tradePair?.asset.symbol ?? Tokens.FALLBACK} Price
                    </span>
                    <MarkPrice pool={position.tradePair} />
                </div>
                <div
                    className={`positions__type position-id-${position.id} ${
                        position.isShort ? 'short' : 'long'
                    }`}
                >
                    <AssetType
                        tradePair={position.tradePair}
                        isShort={position.isShort}
                        leverage={position.leverage}
                    />
                </div>
                <div className='positions__size'>
                    <span className='column-label'>Size</span>
                    <span>
                        {formatAmountWithSuffix(position.volume || 0, {
                            minDecimals: 2,
                            currency: 'USD',
                            minValue: 1_000_000_000,
                        })}
                    </span>
                </div>
                <div className='positions__collateral'>
                    <span className='column-label'>Collateral</span>
                    <span className='value'>
                        {formatAmountWithSuffix(position.margin || 0, {
                            minDecimals: 2,
                            currency: 'USD',
                            minValue: 1_000_000_000,
                        })}
                    </span>
                </div>
                <div className='positions__entry-price'>
                    <span className='column-label'>Entry Price</span>
                    {formatUSD(position.entryPrice || 0, {
                        minDecimals: 2,
                        maxDecimals: position.tradePair.decimalFormatting,
                    })}
                </div>
                <div className='positions__mark-price'>
                    <span className='column-label'>Mark Price</span>
                    <MarkPrice pool={position.tradePair} />
                </div>
                <div className='positions__liquidation-price'>
                    <span className='column-label'>Liq. Price</span>
                    {formatUSD(position.bankruptcyPrice || 0, {
                        minDecimals: 2,
                        maxDecimals: position.tradePair.decimalFormatting,
                    })}
                </div>

                <div className='positions__tpsl'>
                    <span className='column-label'>TP/SL</span>
                    <div>
                        <div>
                            <span>TP: </span>
                            {position.tpsl.takeProfit
                                ? `${formatUSD(position.tpsl.takeProfit.price || 0, {
                                      minDecimals: 2,
                                      maxDecimals: position.tradePair.decimalFormatting,
                                  })}`
                                : '--'}
                        </div>
                        <span className='separator'> / </span>
                        <div>
                            <span>SL: </span>
                            {position.tpsl.stopLoss
                                ? `${formatUSD(position.tpsl.stopLoss.price || 0, {
                                      minDecimals: 2,
                                      maxDecimals: position.tradePair.decimalFormatting,
                                  })}`
                                : '--'}
                        </div>
                    </div>
                </div>
                <div className='positions__pnl'>
                    <span className='column-label'>PNL</span>
                    <div className='pnl-value-wrapper'>
                        <PnlValueWrapper
                            position={position}
                            pnlValues={pnlValues}
                            handleShare={handleShare}
                        />
                    </div>
                </div>
            </Card>
            <div className='positions__action-container'>
                <ClickAwayListener
                    onClickAway={() => !otherModalOpen && isActionShown && hideActions()}
                >
                    <Card
                        className={clsx('action-wrapper', { active: isActionShown })}
                        ref={cardRef}
                        tabIndex={isActionShown ? 0 : -1}
                        style={{
                            visibility:
                                isActionShown || visibility ? 'visible' : 'hidden',
                        }}
                        onTransitionEnd={handleTransitionEnd}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Button
                            variant='transparent'
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                                handleButtonClick(e, position, 'editPositionModal')
                            }
                            tabIndex={isActionShown ? 0 : -1}
                            className='edit-position-button'
                        >
                            <EditIcon />
                            <span>Edit</span>
                            <span>&nbsp;Position</span>
                        </Button>
                        <Button
                            variant='transparent'
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                                handleButtonClick(e, position, 'closePositionModal')
                            }
                            tabIndex={isActionShown ? 0 : -1}
                            className='close-position-button'
                        >
                            <CrossIcon />
                            <span>Close </span>
                            <span>&nbsp;Position</span>
                        </Button>{' '}
                        <Button
                            variant='transparent'
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                                handleShare(e)
                            }
                            tabIndex={isActionShown ? 0 : -1}
                            className='share-position-button'
                        >
                            <ShareIcon />
                            <span>Share</span>
                        </Button>
                        <Button
                            className='position-menu'
                            variant='transparent'
                            onClick={hideActions}
                            tabIndex={isActionShown ? 0 : -1}
                        >
                            <span />
                            <span />
                            <span />
                        </Button>
                    </Card>
                </ClickAwayListener>
                <Button
                    className='position-menu'
                    variant='transparent'
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        showActions();
                        e.stopPropagation();
                    }}
                    tabIndex={isActionShown ? -1 : 0}
                >
                    <span />
                    <span />
                    <span />
                </Button>
            </div>
            {/* mobile */}
            {/* <Button onClick={() => handleButtonClick(position, 'editPositionModal')}>
                    <span>Edit</span>
                    <span>&nbsp;Position</span>
                </Button>
                <Button onClick={() => handleButtonClick(position, 'closePositionModal')}>
                    <span>Close </span>
                    <span>&nbsp;Position</span>
                </Button> */}
        </div>
    );
};

export default PositionRow;

const PnlValueWrapper: React.FC<PnlValueWrapperProps> = ({
    position,
    pnlValues,
    handleShare,
}) => {
    const { account } = useWeb3React();

    // preloading userCreatedreferral so we don't have to wait for it to load on share modals
    useUserCreatedReferral(account);

    return (
        <>
            <div
                className={clsx('pnl-value', {
                    increased: parseUnits(
                        pnlValues?.pnl || '0',
                        config.priceFeedDecimal
                    ).gt('0'),
                    decreased: parseUnits(
                        pnlValues?.pnl || '0',
                        config.priceFeedDecimal
                    ).lt('0'),
                })}
            >
                {pnlValues ? (
                    <>
                        <span>
                            {formatAmountWithSuffix(pnlValues.pnl || 0, {
                                minValue: 10_000_000,
                                minDecimals: 2,
                                currency: 'USD',
                            })}
                            &nbsp;
                        </span>
                        <span>
                            (
                            {parseUnits(
                                pnlValues.pnlPercent,
                                config.priceFeedDecimal
                            ).gte(0)
                                ? '+'
                                : ''}
                            {formatAmountWithSuffix(pnlValues.pnlPercent || 0, {
                                minValue: 10_000,
                                minDecimals: 2,
                            })}
                            %)
                        </span>
                    </>
                ) : (
                    '-'
                )}
            </div>
            <Button variant='transparent' onClick={handleShare}>
                <ShareIcon />
            </Button>
        </>
    );
};
